<template>
    <CustomDialog
        v-model="isVisible"
        :defaultTitle="
            parentCategory
                ? $t('productCategories.createPopup.titleSubcategory')
                : $t('productCategories.createPopup.titleCategory')
        "
        width="800px"
    >
        <template #content>
            <div class="ppv2-p-8">
                <div class="ppv2-grid ppv2-grid-cols-1 md:ppv2-grid-cols-2 ppv2-gap-8">
                    <!-- Name -->
                    <CustomInput
                        v-model="form.name"
                        :label="
                            isSubcategory
                                ? $t('productCategories.createPopup.nameSubcategoryLabel')
                                : $t('productCategories.createPopup.nameLabel')
                        "
                        :placeholder="$t('productCategories.createPopup.namePlaceholder')"
                        required
                        :errorText="errors.name ? errors.name[0] : ''"
                        @input="onNameInput"
                        :state="getState('name')"
                    />
                    <!-- Slug -->
                    <CustomInput
                        v-model="form.slug"
                        :label="$t('productCategories.createPopup.slugLabel')"
                        :placeholder="$t('productCategories.createPopup.slugPlaceholder')"
                        :state="getState('slug')"
                        @input="clearError('slug')"
                        :errorText="errors.slug ? errors.slug[0] : ''"
                    />
                </div>

                <!-- Illustration (Only for Categories) -->
                <div v-if="!isSubcategory" class="ppv2-mt-6">
                    <CustomInputUpload
                        :label="$t('productCategories.createPopup.illustrationLabel')"
                        v-model="form.image"
                        @input="onImageUpload"
                        :description="$t('productCategories.createPopup.chooseImage')"
                        :errorText="errors.image ? errors.image[0] : ''"
                        :state="errors.image ? 'error' : 'default'"
                    />
                </div>
            </div>
        </template>
        <template #actions>
            <BaseButton
                variant="primary"
                @click="submit"
                :disabled="isLoading"
                :customClass="`!ppv2-h-[45px] !ppv2-min-w-[170px]`"
                >{{ $t('common.confirm') }}
            </BaseButton>
        </template>
    </CustomDialog>
</template>

<script>
import BaseInput from '../../../Shared/BaseInput.vue'
import CustomInputUpload from '../../../Inputs/CustomInputUpload.vue'
import normalizeSize from '../../../../utils/normalizeSize'
import axios from 'axios'
import CustomDialog from '../../../Shared/CustomDialog.vue'
import BaseButton from '../../../Shared/BaseButton.vue'
import CustomInput from '../../../Inputs/CustomInput.vue'

export default {
    name: 'CategoryForm',
    components: {
        CustomInput,
        BaseButton,
        CustomDialog,
        BaseInput,
        CustomInputUpload,
    },
    props: {
        parentCategory: {
            type: Object,
            default: null,
        },
        editItem: {
            type: Object,
            default: null,
        },
        value: {
            type: Boolean,
        },
        token: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            isLoading: false,
            form: {
                name: '',
                slug: '',
                image: null,
            },
            errors: {},
        }
    },
    computed: {
        isEdit() {
            return !!this.editItem
        },
        isSubcategory() {
            if (this.editItem) {
                // In the table, subcategories have category_id
                return !!this.editItem.category_id
            }
            return !!this.parentCategory
        },
        isVisible: {
            get() {
                return this.value
            },
            set(val) {
                this.$emit('input', val)
            },
        },
    },
    created() {
        if (this.editItem) {
            this.form.name = this.editItem.name || ''
            this.form.slug = this.editItem.slug || ''
            this.form.image = this.editItem.image || null
        }
    },
    methods: {
        normalizeSize,
        getState(field) {
            if (this.errors[field]) return 'error'
            return 'default'
        },
        clearError(field) {
            if (this.errors[field]) {
                this.$delete(this.errors, field)
            }
        },
        onNameInput(val) {
            // Auto-generate slug if we are creating
            if (val && !this.isEdit) {
                this.form.slug = this.generateSlug(val)
            }
            this.clearError('name')
        },
        generateSlug(text) {
            return text
                .toLowerCase()
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '')
                .replace(/[^a-z0-9]+/g, '-')
                .replace(/(^-|-$)+/g, '')
        },
        validateForm() {
            const errors = {}

            if (!this.form.name || this.form.name.trim() === '') {
                errors.name = [this.$t('productCategories.validation.nameRequired')]
            }

            return errors
        },
        async submit() {
            this.errors = this.validateForm()

            if (Object.keys(this.errors).length > 0) {
                const firstError = Object.values(this.errors)[0][0]
                this.$toast.error(firstError)
                return
            }

            this.isLoading = true
            this.errors = {}

            try {
                const formData = new FormData()
                formData.append('name', this.form.name)
                formData.append('slug', this.form.slug)

                if (this.form.image instanceof File) {
                    formData.append('image', this.form.image)
                }

                let endpoint = ''
                if (this.isEdit) {
                    const type = this.isSubcategory ? 'subcategory' : 'category'
                    endpoint = `/api/v3/product_category/update/${type}/${this.editItem.id}`
                    formData.append('_method', 'PATCH')
                } else {
                    if (this.parentCategory) {
                        endpoint = '/api/v3/product_category/store-subcategory'
                        formData.append('category_id', this.parentCategory.id)
                    } else {
                        endpoint = '/api/v3/product_category/store'
                    }
                }

                const response = await axios.post(endpoint, formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                        Authorization: `Bearer ${this.token}`,
                    },
                })

                if (response.data.success) {
                    this.$toast.success(
                        this.isEdit
                            ? 'Modifié avec succès'
                            : this.isSubcategory
                              ? this.$t('productCategories.createPopup.successSubcategory')
                              : this.$t('productCategories.createPopup.successCategory')
                    )
                    this.$emit('success', response.data.data)
                }
            } catch (error) {
                this.$toast.error(error.response.data.message)
                if (error.response && error.response.status === 422) {
                    this.errors = error.response.data.errors
                    const firstError = Object.values(this.errors)[0][0]
                    this.$toast.error(firstError)
                } else {
                    this.$toast.error('Une erreur est survenue')
                }
            } finally {
                this.isLoading = false
            }
        },
        onImageUpload(file) {
            this.form.image = file
        },
    },
}
</script>
